import {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import LucideIcon from '../Icons/LucideIcon';
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { postMediaBigImages } from '@/src/apis/rest/mediaRest';
import { Media } from '@/src/models/MeidaDto';

export interface SingleImageUploadProps {
  onChange?: (media: Media, type: 'edit' | 'create') => void;
  topAccessary?: ({
    handleEdit,
    handleRemove,
  }: {
    handleEdit: () => void;
    handleRemove: () => void;
  }) => ReactNode;
  value?: string;
  containerClassName?: string;
  inputClassName?: string;
  accept?: string;
  inputProps?: InputHTMLAttributes<HTMLElement>;
  disabled?: boolean;
}

function SingleImageUpload({
  containerClassName,
  topAccessary,
  value,
  onChange,
  inputClassName,
  accept,
  inputProps,
  disabled = false,
}: SingleImageUploadProps) {
  const ref = useRef<HTMLInputElement | null>(null);
  const [image, setImage] = useState<string | null>(value ?? null);

  const uploadImageMutation = useMutation({ mutationFn: postMediaBigImages });

  const handleRemove = () => {
    setImage(null);
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      Array.from(e.target.files || []).forEach(file => {
        formData.append('image', file);
      });
      formData.append('folder', 'commons/');
      formData.append('service', 'BOS_WEB');

      const response = await uploadImageMutation.mutateAsync(formData);

      await onChange?.(response.rows[0], image ? 'edit' : 'create');
      onChange || setImage(response.rows[0].bufferFullUrl);

      setIsLoading(false);
    } catch (e) {
      setImage(null);

      setIsLoading(false);
    }
  };

  useEffect(() => {
    setImage(value || null);
  }, [value]);

  const currentImageImageStyle = !image
    ? 'relative h-[128px] w-[128px] rounded-lg border border-dashed border-blue2 bg-blue0'
    : 'relative h-[128px] w-[128px] rounded-lg border';

  const inputCursorStype = disabled ? '' : 'cursor-pointer';

  return (
    <div
      tabIndex={0}
      className={twMerge(currentImageImageStyle, containerClassName)}
    >
      {!image && (
        <LucideIcon
          className="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] transform "
          name="ImagePlus"
          color="blue7"
        />
      )}

      {isLoading ? (
        <div className="relative flex  h-full w-full items-center justify-center rounded-lg border-dashed border-blue2 bg-blue0">
          <LucideIcon
            name="Loader2"
            size={20}
            color="blue7"
            className="animate-spin"
          />
        </div>
      ) : (
        image && (
          <div className="relative flex h-full w-full">
            <div className="absolute right-[2px] top-[2px] z-30">
              {topAccessary?.({
                handleEdit: () => {
                  ref.current?.click();
                },
                handleRemove,
              })}
            </div>
            <img
              className="h-full w-full rounded-lg object-contain"
              src={image}
              alt="preview"
            />
          </div>
        )
      )}

      <input
        type="file"
        ref={ref}
        value=""
        className={twMerge(
          'absolute left-[50%] top-[50%] h-full w-full translate-x-[-50%]  translate-y-[-50%] transform opacity-0',
          inputCursorStype,
          inputClassName,
        )}
        onChange={e => {
          handleUpload(e);
        }}
        disabled={disabled || isLoading}
        accept={accept ?? 'image/gif, image/jpeg, image/png, image/jpg'}
        {...inputProps}
      />
    </div>
  );
}

export default SingleImageUpload;
